<template>
  <div class="rights-card" @mouseenter="hovered = true" @mouseleave="hovered = false">
    <div v-if="rights.right_display" class="rights-card__content">
      <div class="rights-card__intro-bg intro-bg">
        <rightsView.TitleView
          :titleClass="rightsView.titleClass"
          :rights="rights"
          :hovered="hovered"
        />
      </div>
      <div
        class="rights-card__inner"
        :class="{
          'rights-card__inner--chinese': isChinese(),
        }"
      >
        <div
          class="rights-card__actions"
          :class="{
            'rights-card__actions--foreign': !isChinese(),
          }"
        >
          <!-- 价格、按钮 -->
          <rightsView.PriceView :rights="rights" :title-class="rightsView.titleClass" />
          <rightsView.ButtonView />
        </div>
        <div
          class="rights-card__details"
          :class="{
            'rights-card__details--foreign': !isChinese(),
          }"
        >
          <div v-for="(detail, index) in firstCard" :key="index">
            <div v-if="detail?.items?.length" class="rights-card__desc-wrapper desc-wrapper">
              <div v-if="isChinese()" class="rights-card__desc-title desc-title">
                {{ detail?.title }}
              </div>
              <template v-for="(item, index) in detail?.items" :key="index">
                <div
                  v-if="item?.rights_content?.[rights.rights_type]?.title"
                  class="rights-card__desc-line desc-line"
                >
                  <div class="rights-card__desc-content">
                    <div class="rights-card__desc-item">
                      <iconpark-icon
                        v-if="isChinese()"
                        :class="[
                          'rights-card__icon',
                          isTeam ? 'rights-card__icon--team' : 'rights-card__icon--success',
                        ]"
                        size="20"
                        name="right-icon"
                      ></iconpark-icon>
                      <div v-else class="rights-card__dot-wrapper">
                        <div class="rights-card__dot"></div>
                      </div>
                      <div
                        class="rights-card__desc-text"
                        v-html="item.rights_content[rights.rights_type]?.title"
                      ></div>
                      <a-popover
                        v-if="detail?.idx === 0 && index === 0 && !isGlobalVersion()"
                        :overlay-inner-style="{ width: '500px' }"
                        placement="right"
                        color="#ffffff"
                      >
                        <template #title>
                          <AuthorizationDifferencesContent />
                        </template>
                        <iconpark-icon
                          name="icon-circle-info"
                          class="rights-card__info-icon rights-card__info-icon--clickable"
                        />
                      </a-popover>
                      <Tooltip
                        v-if="item.rights_content[rights.rights_type]?.hover"
                        placement="right"
                      >
                        <template #content>
                          {{ item.rights_content[rights.rights_type]?.hover }}
                        </template>
                        <iconpark-icon name="icon-circle-info" class="rights-card__info-icon" />
                      </Tooltip>
                    </div>
                    <div
                      v-if="item.rights_content[rights.rights_type]?.subtitle"
                      class="rights-card__subtitle rights-info-subtitle"
                    >
                      {{ item.rights_content[rights.rights_type]?.subtitle }}
                      <Tooltip
                        v-if="item.rights_content[rights.rights_type]?.subhover"
                        placement="right"
                      >
                        <template #content>
                          {{ item.rights_content[rights.rights_type]?.subhover }}
                        </template>
                        <iconpark-icon name="icon-circle-info" class="rights-card__info-icon" />
                      </Tooltip>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import Tooltip from '@rendora/design/components/tooltip/Tooltip.vue';
import { ORG_KEY, USER_LEVEL_KEY, type IEquity, type LoginInfo } from '~/api/interface';
import type { IRightsCompare } from '~/api/interface/rights';
import { useRightsConfig } from '~/composables/rights';
import AuthorizationDifferencesContent from '~/features/paymentDesk/components/buy-rights/authorization-differences-content.vue';
import { getRightsView } from './rights-view';

const props = defineProps<{
  rights: IEquity;
  accountList: LoginInfo[];
  compareRights: IRightsCompare[];
}>();

const userInfo = useUserInfo();
const overseas = useRightsConfig();

const hovered = ref(false);

const isTaste = computed(() => props.rights.rights_type === USER_LEVEL_KEY.TASTE);
const isTeam = computed(
  () =>
    props.rights.rights_type === USER_LEVEL_KEY.TEAM_LIGHT || props.rights.rights_type === ORG_KEY,
);

// TODO 付费订阅页数据展示判断需要更改
const descInfo = computed(() => props.compareRights.filter(o => !o.idx));

const firstCard = computed(() => {
  const first = descInfo.value[0]?.items.filter(item => {
    return !Number(item.rights_content?.[props.rights.rights_type]?.group);
  });

  const second = descInfo.value[0]?.items.filter(item => {
    return Number(item.rights_content?.[props.rights.rights_type]?.group) === 1;
  });

  return [
    {
      ...descInfo.value[0],
      items: first,
    },
    {
      ...descInfo.value[0],
      items: second,
    },
  ];
});

const rightsView = computed(() =>
  getRightsView({
    rights: props.rights,
    userInfo: userInfo.value,
    accountList: props.accountList,
    overseas: isGlobalVersion() ? overseas.value : null,
  }),
);
</script>

<style lang="scss" scoped>
// 主容器
.rights-card {
  position: relative;
}

// 内容区域
.rights-card__content {
  position: relative;
  z-index: 10;
}

// 介绍背景区域
.rights-card__intro-bg {
  position: relative;
}

// 内部容器
.rights-card__inner {
  padding-left: 16px;
  padding-right: 16px;
}

.rights-card__inner--chinese {
  padding-left: 24px;
  padding-right: 24px;
}

// 操作区域（价格、按钮）
.rights-card__actions {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
}

.rights-card__actions--foreign {
  align-items: flex-start;
}

// 详情区域
.rights-card__details {
  margin-top: 0;
}

.rights-card__details--foreign {
  margin-top: 40px;
}

// 描述包装器
.rights-card__desc-wrapper {
  font-size: 14px;
  color: rgba(18, 25, 38, 0.5);
}

// 描述标题
.rights-card__desc-title {
  font-size: 14px;
  line-height: 20px;
  margin: 32px 0 20px 0;
  color: var(--text-1);
  font-weight: var(--x-weight-medium, 500);
}

// 描述行
.rights-card__desc-line {
  display: flex;
  align-items: center;
  font-size: 14px;
  line-height: 20px;
  color: var(--text-1-70);
  margin-bottom: 16px;
}

// 描述内容
.rights-card__desc-content {
  flex: 1;
}

// 描述项
.rights-card__desc-item {
  display: flex;
  align-items: center;
  position: relative;
}

// 图标
.rights-card__icon {
  margin-right: 4px;
}

.rights-card__icon--team {
  color: var(--warning-7, #ffb53d);
}

.rights-card__icon--success {
  color: var(--success-6, #00d5a6);
}

// 点状指示器包装器
.rights-card__dot-wrapper {
  width: 4px;
  height: 4px;
  flex-shrink: 0;
  margin-right: 8px;
}

// 点状指示器
.rights-card__dot {
  width: 4px;
  height: 4px;
  flex-shrink: 0;
  position: absolute;
  top: 8px;
  border-radius: 50%;
  background-color: #71757d;
}

// 信息图标
.rights-card__info-icon {
  color: var(--text-1, rgba(18, 25, 38, 1));
  margin-left: 4px;
  font-size: 16px;
}

.rights-card__info-icon--clickable {
  cursor: pointer;
}

// 副标题
.rights-card__subtitle {
  font-size: 12px;
  margin-top: 12px;
  margin-left: 24px;
  display: flex;
  align-items: center;
}
</style>
